import { NEWS_CATEGORIES } from './newsPublications';

export const NEWS_PUBLICATION_DETAIL_PREFIX = '/novosti-antologia/publikaciya';

export const NEWS_CATEGORY_OPTIONS = NEWS_CATEGORIES.filter((c) => c.id !== 'all');

export const SEO_LIMITS = {
  metaTitle: 60,
  metaDescription: 160,
  geoSummary: 320,
};

export function slugifyNewsTitle(title) {
  const map = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
    й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
    у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
    э: 'e', ю: 'yu', я: 'ya',
  };
  return (title || '')
    .toLowerCase()
    .split('')
    .map((ch) => map[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

export function emptyNewsForm() {
  return {
    slug: '',
    category: 'antologia',
    title: '',
    description: '',
    full_text: '',
    preview_image: '',
    main_image: '',
    display_date: '',
    published_at: '',
    author: 'Антология',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    canonical_path: '',
    og_image: '',
    geo_summary: '',
    key_takeaways: [''],
    faq_items: [{ question: '', answer: '' }],
    external_href: '',
    is_published: true,
    is_featured: false,
    sort_order: 0,
  };
}

export function newsItemToForm(item) {
  if (!item) return emptyNewsForm();
  return {
    slug: item.slug || '',
    category: item.category || 'antologia',
    title: item.title || '',
    description: item.description || '',
    full_text: item.full_text || item.fullText || '',
    preview_image: item.preview_image || item.previewImage || '',
    main_image: item.main_image || item.mainImage || '',
    display_date: item.display_date || item.displayDate || item.date || '',
    published_at: (item.published_at || item.publishedAt || '').slice(0, 10),
    author: item.author || 'Антология',
    meta_title: item.meta_title || item.metaTitle || '',
    meta_description: item.meta_description || item.metaDescription || '',
    meta_keywords: item.meta_keywords || item.metaKeywords || '',
    canonical_path: item.canonical_path || item.canonicalPath || '',
    og_image: item.og_image || item.ogImage || '',
    geo_summary: item.geo_summary || item.geoSummary || '',
    key_takeaways: (item.key_takeaways || item.keyTakeaways || []).length
      ? (item.key_takeaways || item.keyTakeaways)
      : [''],
    faq_items: (item.faq_items || item.faqItems || []).length
      ? (item.faq_items || item.faqItems)
      : [{ question: '', answer: '' }],
    external_href: item.external_href || '',
    is_published: item.is_published ?? item.isPublished ?? true,
    is_featured: item.is_featured ?? item.isFeatured ?? false,
    sort_order: item.sort_order ?? item.sortOrder ?? 0,
  };
}

export function buildNewsPayload(form) {
  const takeaways = (form.key_takeaways || [])
    .map((t) => (t || '').trim())
    .filter(Boolean);
  const faqItems = (form.faq_items || [])
    .filter((item) => (item.question || '').trim())
    .map((item) => ({
      question: item.question.trim(),
      answer: (item.answer || '').trim(),
    }));

  const slug = (form.slug || '').trim().toLowerCase();
  const canonical = (form.canonical_path || '').trim() || `${NEWS_PUBLICATION_DETAIL_PREFIX}/${slug}`;

  return {
    slug,
    category: form.category,
    title: (form.title || '').trim(),
    description: (form.description || '').trim(),
    full_text: form.full_text || '',
    preview_image: form.preview_image || null,
    main_image: form.main_image || null,
    display_date: (form.display_date || '').trim(),
    published_at: form.published_at || null,
    author: (form.author || 'Антология').trim(),
    meta_title: (form.meta_title || '').trim(),
    meta_description: (form.meta_description || '').trim(),
    meta_keywords: (form.meta_keywords || '').trim(),
    canonical_path: canonical,
    og_image: form.og_image || form.main_image || form.preview_image || null,
    geo_summary: (form.geo_summary || '').trim(),
    key_takeaways: takeaways,
    faq_items: faqItems,
    external_href: (form.external_href || '').trim() || null,
    is_published: !!form.is_published,
    is_featured: !!form.is_featured,
    sort_order: Number(form.sort_order) || 0,
  };
}

export function suggestSeoFromForm(form) {
  const title = (form.title || '').trim();
  const description = (form.description || '').trim();
  const geo = (form.geo_summary || description).trim();
  return {
    meta_title: (form.meta_title || title).slice(0, SEO_LIMITS.metaTitle),
    meta_description: (form.meta_description || description).slice(0, SEO_LIMITS.metaDescription),
    geo_summary: geo.slice(0, SEO_LIMITS.geoSummary),
    meta_keywords: form.meta_keywords || '',
  };
}

export function publicationPreviewPath(slug) {
  return `${NEWS_PUBLICATION_DETAIL_PREFIX}/${slug}`;
}

const IMAGE_MIME_BY_EXT = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

/** Преобразует сохранённый URL в объект для MediaManager (превью в админке). */
export function mediaPreviewFilesFromUrl(url) {
  if (!url || typeof url !== 'string') return [];
  const trimmed = url.trim();
  if (!trimmed) return [];

  const name = trimmed.split('/').pop() || 'image';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';

  return [{
    id: trimmed,
    url: trimmed,
    name,
    type: IMAGE_MIME_BY_EXT[ext] || 'image/png',
  }];
}
